// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Pressable, StyleSheet, View } from 'react-native';
import { Text } from '@/components/Themed';
import appColors from '@/theme/colors';

export type HoleCellState = 'locked' | 'selected' | 'selectable' | 'disabled' | 'plain';

export type HoleGridCell = {
  holeNumber: number;
  points: number | null;
  sublabel?: string;
  state: HoleCellState;
};

type Props = {
  cells: HoleGridCell[];
  onPressHole?: (holeNumber: number) => void;
  testIDPrefix?: string;
  // Fixed number of cells per row; without it the grid wraps freely.
  columns?: number;
};

// Even rows read best: only 18, 9 or 6 columns. 18 puts a full round on one
// row once there's room; narrower screens fall back to two or three rows.
export function resolveHoleGridColumns(availableWidth: number): 18 | 9 | 6 {
  if (availableWidth >= 640) return 18;
  if (availableWidth >= 380) return 9;
  return 6;
}

// Compact hole grid shared by the freeze lock screen and the leaderboard hole
// breakdowns. Locked cells are gold, selected cells filled.
export default function HolePointsGrid({ cells, onPressHole, testIDPrefix, columns }: Props) {
  const renderCell = (cell: HoleGridCell, fixed: boolean) => {
    const interactive = onPressHole && (cell.state === 'selectable' || cell.state === 'selected');
    return (
      <Pressable
        key={cell.holeNumber}
        style={[styles.cell, fixed && styles.cellFixed, stateStyles[cell.state]]}
        disabled={!interactive}
        onPress={interactive ? () => onPressHole(cell.holeNumber) : undefined}
        accessibilityRole={interactive ? 'checkbox' : undefined}
        accessibilityState={interactive ? { checked: cell.state === 'selected' } : undefined}
        testID={testIDPrefix ? `${testIDPrefix}-hole-${cell.holeNumber}` : undefined}
      >
        <Text style={[styles.holeNumber, stateTextStyles[cell.state]]}>
          {cell.holeNumber}
          {cell.state === 'locked' ? ' \u{1F512}' : ''}
        </Text>
        <Text style={[styles.points, stateTextStyles[cell.state]]}>
          {cell.points === null ? '–' : String(cell.points)}
        </Text>
        {cell.sublabel ? (
          <Text style={[styles.sublabel, stateTextStyles[cell.state]]}>{cell.sublabel}</Text>
        ) : null}
      </Pressable>
    );
  };

  if (columns && columns > 0) {
    const rows: HoleGridCell[][] = [];
    for (let i = 0; i < cells.length; i += columns) {
      rows.push(cells.slice(i, i + columns));
    }
    return (
      <View style={styles.rows}>
        {rows.map((row, rowIndex) => (
          <View key={rowIndex} style={styles.row}>
            {row.map((cell) => renderCell(cell, true))}
            {Array.from({ length: columns - row.length }, (_, index) => (
              <View key={`filler-${index}`} style={styles.fillerCell} />
            ))}
          </View>
        ))}
      </View>
    );
  }

  return <View style={styles.grid}>{cells.map((cell) => renderCell(cell, false))}</View>;
}

const styles = StyleSheet.create({
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    backgroundColor: 'transparent',
  },
  rows: {
    gap: 6,
    backgroundColor: 'transparent',
  },
  row: {
    flexDirection: 'row',
    gap: 6,
    backgroundColor: 'transparent',
  },
  cell: {
    width: '10%',
    minWidth: 34,
    flexGrow: 1,
    borderRadius: 8,
    borderWidth: 1,
    paddingVertical: 4,
    alignItems: 'center',
    backgroundColor: appColors.surface.input,
    borderColor: appColors.border.strong,
  },
  cellFixed: {
    width: 'auto',
    minWidth: 0,
    flex: 1,
  },
  fillerCell: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  holeNumber: {
    fontSize: 11,
    color: appColors.text.muted,
  },
  points: {
    fontSize: 15,
    fontWeight: '600',
    color: appColors.text.primary,
  },
  sublabel: {
    fontSize: 9,
    color: appColors.text.muted,
  },
});

const stateStyles: Record<HoleCellState, object> = StyleSheet.create({
  locked: {
    backgroundColor: appColors.accent.goldSoft,
    borderColor: appColors.accent.goldBorder,
  },
  selected: {
    backgroundColor: appColors.accent.primary,
    borderColor: appColors.accent.primary,
  },
  selectable: {},
  disabled: {
    opacity: 0.45,
  },
  plain: {
    backgroundColor: appColors.surface.elevated,
    borderColor: appColors.border.default,
  },
});

const stateTextStyles: Record<HoleCellState, object> = StyleSheet.create({
  locked: { color: appColors.accent.gold },
  selected: { color: appColors.text.inverse },
  selectable: {},
  disabled: {},
  plain: {},
});
